import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { financialsService } from '@/services/financials-graphql-service';
import type { PendingFilters } from '@/contexts/RightPanelContext';

const STATUS_OPTIONS = [
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Completed', value: 'Completed' },
];

const PERIOD_OPTIONS = [
  { label: 'Period 1', value: '1' },
  { label: 'Period 2', value: '2' },
  { label: 'Period 3', value: '3' },
  { label: 'Period 4', value: '4' },
  { label: 'Period 5', value: '5' },
  { label: 'Period 6', value: '6' },
  { label: 'Period 7', value: '7' },
  { label: 'Period 8', value: '8' },
  { label: 'Period 9', value: '9' },
  { label: 'Period 10', value: '10' },
  { label: 'Period 11', value: '11' },
  { label: 'Period 12', value: '12' },
];

export const RevaluationFiltersTab: React.FC = () => {
  const { 
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    applyFilters,
    clearAllFilters,
  } = useRightPanel();

  const [yearOptions, setYearOptions] = useState<{ label: string; value: string }[]>([]);
  const [isLoadingYears, setIsLoadingYears] = useState(false);

  const fetchYearOptions = useCallback(async () => {
    setIsLoadingYears(true);
    try {
      const years = await financialsService.getSystemYears('desc');
      setYearOptions(years.map(y => ({ label: y.label, value: y.nominal.toString() })));
    } catch {
      setYearOptions([]);
    } finally {
      setIsLoadingYears(false);
    }
  }, []);

  useEffect(() => {
    fetchYearOptions();
  }, [fetchYearOptions]);

  // Local state for filter inputs
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [adjustmentMin, setAdjustmentMin] = useState('');
  const [adjustmentMax, setAdjustmentMax] = useState('');
  const [assetCountMin, setAssetCountMin] = useState('');
  const [assetCountMax, setAssetCountMax] = useState('');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [postedBySearch, setPostedBySearch] = useState('');
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [preparedBySearch, setPreparedBySearch] = useState('');
  const [preparedDateRange, setPreparedDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [totalCurrentNBVMin, setTotalCurrentNBVMin] = useState('');
  const [totalCurrentNBVMax, setTotalCurrentNBVMax] = useState('');
  const [totalRevaluedNBVMin, setTotalRevaluedNBVMin] = useState('');
  const [totalRevaluedNBVMax, setTotalRevaluedNBVMax] = useState('');

  // Sync local state with pending filters when they change externally
  useEffect(() => {
    if (pendingFilters) {
      setSelectedStatuses(pendingFilters.selectedStatuses || []);
      setDateRange(pendingFilters.transactionDateRange || { from: undefined, to: undefined });
      setAdjustmentMin(pendingFilters.transactionAmountMin?.toString() || '');
      setAdjustmentMax(pendingFilters.transactionAmountMax?.toString() || '');
      setSelectedPeriods(pendingFilters.selectedPeriods || []);
      setSelectedYears(pendingFilters.selectedYears || []);
      setPreparedBySearch(pendingFilters.preparedBySearch || '');
      setPreparedDateRange(pendingFilters.preparedDateRange || { from: undefined, to: undefined });
      setTotalCurrentNBVMin(pendingFilters.totalCurrentNBVMin || '');
      setTotalCurrentNBVMax(pendingFilters.totalCurrentNBVMax || '');
      setTotalRevaluedNBVMin(pendingFilters.totalRevaluedNBVMin || '');
      setTotalRevaluedNBVMax(pendingFilters.totalRevaluedNBVMax || '');
    }
  }, [pendingFilters]);

  // Update pending filters whenever local state changes
  useEffect(() => {
    const filters: Partial<PendingFilters> = {
      selectedStatuses,
      transactionDateRange: dateRange,
      transactionAmountMin: adjustmentMin ? parseFloat(adjustmentMin) : undefined,
      transactionAmountMax: adjustmentMax ? parseFloat(adjustmentMax) : undefined,
      selectedPeriods,
      selectedYears,
      preparedBySearch,
      preparedDateRange,
      assetCountMin,
      assetCountMax,
      totalCurrentNBVMin,
      totalCurrentNBVMax,
      totalRevaluedNBVMin,
      totalRevaluedNBVMax,
    };
    setPendingFilters(filters as PendingFilters);
  }, [selectedStatuses, dateRange, adjustmentMin, adjustmentMax, assetCountMin, assetCountMax, referenceSearch, postedBySearch, selectedPeriods, selectedYears, preparedBySearch, preparedDateRange, totalCurrentNBVMin, totalCurrentNBVMax, totalRevaluedNBVMin, totalRevaluedNBVMax, setPendingFilters]);

  // Calculate active filter count
  const activeCount = useMemo(() => {
    let count = 0;
    if (selectedStatuses.length > 0) count++;
    if (dateRange.from || dateRange.to) count++;
    if (adjustmentMin || adjustmentMax) count++;
    if (assetCountMin || assetCountMax) count++;
    if (referenceSearch) count++;
    if (postedBySearch) count++;
    if (selectedPeriods.length > 0) count++;
    if (selectedYears.length > 0) count++;
    if (preparedBySearch) count++;
    if (preparedDateRange.from || preparedDateRange.to) count++;
    if (totalCurrentNBVMin || totalCurrentNBVMax) count++;
    if (totalRevaluedNBVMin || totalRevaluedNBVMax) count++;
    return count;
  }, [selectedStatuses, dateRange, adjustmentMin, adjustmentMax, assetCountMin, assetCountMax, referenceSearch, postedBySearch, selectedPeriods, selectedYears, preparedBySearch, preparedDateRange, totalCurrentNBVMin, totalCurrentNBVMax, totalRevaluedNBVMin, totalRevaluedNBVMax]);

  useEffect(() => {
    setActiveFilterCount(activeCount);
  }, [activeCount, setActiveFilterCount]);

  const handleClearAll = () => {
    setSelectedStatuses([]);
    setDateRange({ from: undefined, to: undefined });
    setAdjustmentMin('');
    setAdjustmentMax('');
    setAssetCountMin('');
    setAssetCountMax('');
    setReferenceSearch('');
    setPostedBySearch('');
    setSelectedPeriods([]);
    setSelectedYears([]);
    setPreparedBySearch('');
    setPreparedDateRange({ from: undefined, to: undefined });
    setTotalCurrentNBVMin('');
    setTotalCurrentNBVMax('');
    setTotalRevaluedNBVMin('');
    setTotalRevaluedNBVMax('');
    clearAllFilters();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Filters */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Status */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Status</Label>
            <MultiSelect
              options={STATUS_OPTIONS}
              value={selectedStatuses}
              onValueChange={setSelectedStatuses}
              placeholder="All statuses"
            />
          </div>

          <Separator />

          {/* Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date Range</Label>
            <DateRangePicker
              from={dateRange.from}
              to={dateRange.to}
              onFromChange={(date) => setDateRange(prev => ({ ...prev, from: date }))}
              onToChange={(date) => setDateRange(prev => ({ ...prev, to: date }))}
            />
          </div>

          <Separator />

          {/* Adjustment Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Adjustment Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={adjustmentMin}
                  onChange={(e) => setAdjustmentMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={adjustmentMax}
                  onChange={(e) => setAdjustmentMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Asset Count Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Asset Count Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={assetCountMin}
                  onChange={(e) => setAssetCountMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={assetCountMax}
                  onChange={(e) => setAssetCountMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Reference Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Reference</Label>
            <Input
              placeholder="Search by reference..."
              value={referenceSearch}
              onChange={(e) => setReferenceSearch(e.target.value)}
              className="h-9"
            />
          </div>

          <Separator />

          {/* Posted By Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Posted By</Label>
            <Input
              placeholder="Search by user..."
              value={postedBySearch}
              onChange={(e) => setPostedBySearch(e.target.value)}
              className="h-9"
            />
          </div>

          <Separator />

          {/* Period Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Period</Label>
            <MultiSelect
              options={PERIOD_OPTIONS}
              value={selectedPeriods}
              onValueChange={setSelectedPeriods}
              placeholder="All periods"
            />
          </div>

          <Separator />

          {/* Year Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Year</Label>
            <MultiSelect
              options={yearOptions}
              value={selectedYears}
              onValueChange={setSelectedYears}
              placeholder={isLoadingYears ? "Loading years..." : "All years"}
            />
          </div>

          <Separator />

          {/* Prepared By Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Prepared By</Label>
            <Input
              placeholder="Search by user..."
              value={preparedBySearch}
              onChange={(e) => setPreparedBySearch(e.target.value)}
              className="h-9"
            />
          </div>

          <Separator />

          {/* Prepared Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Prepared Date Range</Label>
            <DateRangePicker
              from={preparedDateRange.from}
              to={preparedDateRange.to}
              onFromChange={(date) => setPreparedDateRange(prev => ({ ...prev, from: date }))}
              onToChange={(date) => setPreparedDateRange(prev => ({ ...prev, to: date }))}
            />
          </div>

          <Separator />

          {/* Total Current NBV Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Total Current NBV Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={totalCurrentNBVMin}
                  onChange={(e) => setTotalCurrentNBVMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={totalCurrentNBVMax}
                  onChange={(e) => setTotalCurrentNBVMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Total Revalued NBV Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Total Revalued NBV Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={totalRevaluedNBVMin}
                  onChange={(e) => setTotalRevaluedNBVMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={totalRevaluedNBVMax}
                  onChange={(e) => setTotalRevaluedNBVMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex-shrink-0 border-t bg-muted/20 p-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="flex-1"
            disabled={activeCount === 0}
          >
            Clear All
          </Button>
          <Button
            onClick={applyFilters}
            className="flex-1"
            disabled={activeCount === 0}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};
